import type { ReactNode } from 'react'
import { Field } from '../controls/Field'
import { SkeletonText } from '../controls/Skeleton'
import { SaveBar } from '../work/ActionBar'
import { cx } from '../controls/cx'
import type { SettingsFormState } from './settingsFormState'
import s from './SettingsForm.module.css'

/**
 * SettingsForm — the GEOMETRY half of the one settings recipe (#1680 slice 1).
 *
 * Closing acceptance on the live-deployment defect: "module settings and
 * platform settings consume the same `SettingsForm` recipe, including loading,
 * dirty, saving, saved, validation and refusal states", with "desktop field
 * rows [using] a bounded label/help column and a flexible control column" and
 * "narrow viewports stack label/help above the control, with no clipped help,
 * horizontal overflow or hidden save/reset action".
 *
 * Measured across the console's six settings panels before this was written:
 * DIRTY is exhibited by one of them, SAVED by one, a form-level DISCARD by
 * none, and VALIDATION by none at all. So this is not a re-layout of an
 * existing recipe — four of the six named states have no incumbent.
 *
 * ── Three things this deliberately does NOT do ──────────────────────────────
 *
 * 1. It does not choose a control from a field kind. The host passes the
 *    rendered control in. A per-kind editor family is #1679 slice 2's typed
 *    editor registry, and building a second one here is how a platform ends up
 *    with two. When that registry lands, a caller passes its output into
 *    `control` and nothing here changes.
 *
 * 2. It does not draw its own action row. `SaveBar` is the platform's save bar
 *    and already owns sticky/responsive placement, the discard affordance and a
 *    notice slot — a second one would be the same duplication this issue is
 *    about. SaveBar gained one optional prop for this: a caller-supplied
 *    status, because "Unsaved changes" is the wrong sentence for four of the
 *    six states.
 *
 * 3. It does not CLASSIFY a failed save. Whether a rejection was a refusal, an
 *    outage or a bug is a transport judgement the host makes — `classifyWrite`
 *    and `WriteFailureNotice` live in the app, not here, which is the seam
 *    `StateBoundary` states ("hosts classify … and supply localized copy").
 *    The form knows only that the save did not happen; the host passes the
 *    sentence saying why into `notice`.
 *
 * ── The bounded column is the whole point of the row ─────────────────────────
 *
 * `Field orientation="row"` supplies the geometry, and `Field` supplies the
 * label association the console's hand-rolled settings rows never had:
 * `DeclaredSettings.tsx` rendered `<div>{label}</div>` beside an `<Input>` with
 * nothing tying the two together, so every declared setting on the live
 * deployment reached a screen reader with no accessible name.
 */

export interface SettingsFieldSpec {
  /** the form key this row edits — the same key `SettingsFormState.value`/`set` take */
  key: string
  label: string
  /** guidance, rendered under the label at a bounded measure and announced with the control */
  help?: ReactNode
  /** the rendered control. The host owns which control a kind gets; see the note above. */
  control: ReactNode
  tag?: string | null
  required?: boolean
}

export interface SettingsSectionSpec {
  /** stable id, used as the anchor target for section navigation */
  id: string
  title: string
  description?: ReactNode
  fields: readonly SettingsFieldSpec[]
  /** rendered after the rows — a diagnostic, a table, anything the section owns */
  extra?: ReactNode
}

export interface SettingsFormProps {
  sections: readonly SettingsSectionSpec[]
  form: SettingsFormState
  saveLabel?: string
  /**
   * The host's already-classified sentence about a failed save — e.g. the
   * console's `<WriteFailureNotice>`. Rendered in the save bar's notice slot.
   */
  notice?: ReactNode
  /** shown in place of the sections when nothing is declared */
  empty?: ReactNode
  testid?: string
}

/** The one sentence the save bar says about where the form stands. */
export function settingsFormStatus(form: SettingsFormState): string {
  switch (form.phase) {
    case 'loading': return 'Reading…'
    case 'saving': return 'Saving…'
    case 'saved': return 'Saved.'
    case 'invalid': return 'Nothing was sent — check the highlighted fields.'
    case 'refused': return 'Nothing was saved.'
    case 'dirty': return `${form.dirtyKeys.length} unsaved ${form.dirtyKeys.length === 1 ? 'change' : 'changes'}.`
    default: return 'No unsaved changes.'
  }
}

export function SettingsForm({ sections, form, saveLabel = 'Save changes', notice, empty, testid }: SettingsFormProps) {
  const busy = form.phase === 'saving'
  const loud = form.phase === 'refused' || form.phase === 'invalid'

  if (form.phase === 'loading') {
    return (
      <div className={s.form} data-testid={testid} data-phase="loading" aria-busy="true">
        <SkeletonText lines={6} />
      </div>
    )
  }

  return (
    <form
      className={s.form}
      data-testid={testid}
      data-phase={form.phase}
      noValidate
      onSubmit={(event) => { event.preventDefault(); void form.save() }}
    >
      {sections.length === 0 && empty}

      {sections.map((section) => (
        <section key={section.id} id={section.id} className={s.section} data-testid={`settings-section-${section.id}`}>
          <div className={s.sectionHead}>
            <h2 className={s.sectionTitle}>{section.title}</h2>
            {section.description && <p className={s.sectionDesc}>{section.description}</p>}
          </div>
          <div className={s.rows}>
            {section.fields.map((field) => (
              <div key={field.key} className={s.row} data-dirty={form.dirtyKeys.includes(field.key) || undefined}>
                <Field
                  orientation="row"
                  label={field.label}
                  tag={field.tag}
                  required={field.required}
                  hint={field.help}
                  error={form.errors[field.key]}
                >
                  {field.control}
                </Field>
              </div>
            ))}
          </div>
          {section.extra}
        </section>
      ))}

      <SaveBar
        state="dirty"
        status={(
          <span
            className={cx(s.status, loud && s.loud)}
            data-testid={testid ? `${testid}-status` : undefined}
            /* A refusal and a blocked save are announced when they appear. A
               dirty COUNT is not: it changes on every keystroke, and a live
               region that fires per character is worse than silence. */
            role={loud ? 'alert' : undefined}
            aria-live={form.phase === 'saved' ? 'polite' : undefined}
          >
            {settingsFormStatus(form)}
          </span>
        )}
        saving={busy}
        saveDisabled={!form.isDirty || busy}
        saveLabel={saveLabel}
        onDiscard={form.reset}
        notice={notice}
        /* Derived from the form's own testid, because two forms can share a
           page: `?tab=regional` renders the regional panel and the FX panel
           together, and a fixed testid would name both save buttons the same. */
        testId={testid ? `${testid}-actions` : undefined}
      />
    </form>
  )
}
